// src/pages/production/RecipesTab.tsx
//
// Recipes CRUD with ingredient lines (components). Recipes reference Items
// by id. Cost per yield-unit is computed on the fly from current item costs
// — no snapshotting, edits to an item ripple through to all recipes that
// use it. That matches GeniBatch's behavior.

import { useEffect, useMemo, useState } from 'react';
import { Plus, Pencil, Trash2, Search, ChefHat, X, AlertCircle, FileText } from 'lucide-react';
import Modal from '../../components/Modal';
import {
  getItems, getRecipes, saveRecipe, deleteRecipe, calcRecipeCost,
} from '../../services/productionStorage';
import { generateRecipeLabelPdf } from '../../services/productionNutrition';
import type { ProductionItem, ProductionRecipe, RecipeComponent } from '../../types/production';

interface RecipesTabProps {
  slug: string;
}

const UNIT_OPTIONS = ['lb', 'oz', 'kg', 'g', 'gal', 'qt', 'pt', 'cup', 'fl oz', 'ml', 'L', 'ea'];

interface EditState {
  id?: string;
  name: string;
  description: string;
  category: string;
  yieldQty: string;
  yieldUnit: string;
  sellPrice: string;
  notes: string;
  components: Array<{ itemId: string; qty: string; unit: string; notes: string }>;
}

const EMPTY: EditState = {
  name: '', description: '', category: '', yieldQty: '1', yieldUnit: 'lb',
  sellPrice: '', notes: '', components: [],
};

const INPUT_CLASS = 'w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-sm text-white placeholder-slate-500 focus:border-orange-500/50 focus:outline-none';

export default function RecipesTab({ slug }: RecipesTabProps) {
  const [items, setItems] = useState<ProductionItem[]>([]);
  const [recipes, setRecipes] = useState<ProductionRecipe[]>([]);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<EditState | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<ProductionRecipe | null>(null);
  const [labelTarget, setLabelTarget] = useState<ProductionRecipe | null>(null);
  const [labelServing, setLabelServing] = useState('227g');
  const [labelServings, setLabelServings] = useState('8');

  useEffect(() => {
    setItems(getItems(slug));
    setRecipes(getRecipes(slug));
  }, [slug]);

  const itemsById = useMemo(() => {
    const m: Record<string, ProductionItem> = {};
    for (const i of items) m[i.id] = i;
    return m;
  }, [items]);

  const filtered = useMemo(() => {
    if (!search) return recipes;
    const q = search.toLowerCase();
    return recipes.filter((r) =>
      r.name.toLowerCase().includes(q) ||
      (r.description || '').toLowerCase().includes(q) ||
      (r.category || '').toLowerCase().includes(q),
    );
  }, [recipes, search]);

  const recipesWithCost = useMemo(() => {
    return filtered.map((r) => {
      const { totalCost, costPerUnit, hasMissing } = calcRecipeCost(r, itemsById);
      const margin = r.sellPrice && r.sellPrice > 0
        ? ((r.sellPrice - costPerUnit) / r.sellPrice) * 100
        : null;
      return { recipe: r, totalCost, costPerUnit, hasMissing, margin };
    });
  }, [filtered, itemsById]);

  function openNew() { setEditing({ ...EMPTY, components: [] }); }
  function openEdit(r: ProductionRecipe) {
    setEditing({
      id: r.id,
      name: r.name,
      description: r.description || '',
      category: r.category || '',
      yieldQty: String(r.yieldQty),
      yieldUnit: r.yieldUnit,
      sellPrice: r.sellPrice != null ? String(r.sellPrice) : '',
      notes: r.notes || '',
      components: r.components.map((c) => ({
        itemId: c.itemId, qty: String(c.qty), unit: c.unit, notes: c.notes || '',
      })),
    });
  }

  function addLine() {
    if (!editing) return;
    setEditing({
      ...editing,
      components: [...editing.components, { itemId: '', qty: '0', unit: 'lb', notes: '' }],
    });
  }
  function updateLine(idx: number, patch: Partial<EditState['components'][0]>) {
    if (!editing) return;
    const next = [...editing.components];
    next[idx] = { ...next[idx], ...patch };
    // When an item is picked, default the line's unit to the item's default unit
    if (patch.itemId) {
      const it = itemsById[patch.itemId];
      if (it && !next[idx].unit) next[idx].unit = it.unit;
    }
    setEditing({ ...editing, components: next });
  }
  function removeLine(idx: number) {
    if (!editing) return;
    setEditing({ ...editing, components: editing.components.filter((_, i) => i !== idx) });
  }

  function handleSave() {
    if (!editing) return;
    const name = editing.name.trim();
    if (!name) return;

    const components: RecipeComponent[] = editing.components
      .filter((c) => c.itemId)
      .map((c) => ({
        itemId: c.itemId,
        qty: parseFloat(c.qty) || 0,
        unit: c.unit || 'lb',
        notes: c.notes.trim() || undefined,
      }));

    const yieldQty = parseFloat(editing.yieldQty);
    const sellPrice = editing.sellPrice.trim() ? parseFloat(editing.sellPrice) : undefined;

    saveRecipe(slug, {
      id: editing.id,
      name,
      description: editing.description.trim() || undefined,
      category: editing.category.trim() || undefined,
      yieldQty: Number.isFinite(yieldQty) && yieldQty > 0 ? yieldQty : 1,
      yieldUnit: editing.yieldUnit,
      components,
      sellPrice: Number.isFinite(sellPrice as number) ? sellPrice : undefined,
      notes: editing.notes.trim() || undefined,
    });
    setRecipes(getRecipes(slug));
    setEditing(null);
  }

  function handleDelete() {
    if (!confirmDelete) return;
    deleteRecipe(slug, confirmDelete.id);
    setRecipes(getRecipes(slug));
    setConfirmDelete(null);
  }

  function handleGenerateLabel() {
    if (!labelTarget) return;
    const servings = parseInt(labelServings, 10) || 1;
    const blob = generateRecipeLabelPdf(labelTarget, itemsById, {
      servingSize: labelServing.trim() || '227g',
      servingsPerContainer: servings,
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const safeName = labelTarget.name.replace(/[^a-z0-9-]+/gi, '_');
    a.href = url;
    a.download = `nutrition-label-${safeName}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
    setLabelTarget(null);
  }

  // Live cost preview inside the modal — reuses calcRecipeCost on the
  // in-flight edit state so the user sees the effect of each line change.
  const previewCost = useMemo(() => {
    if (!editing) return null;
    const fakeRecipe: ProductionRecipe = {
      id: editing.id || 'preview',
      name: editing.name || 'preview',
      yieldQty: parseFloat(editing.yieldQty) || 1,
      yieldUnit: editing.yieldUnit,
      components: editing.components
        .filter((c) => c.itemId)
        .map((c) => ({
          itemId: c.itemId,
          qty: parseFloat(c.qty) || 0,
          unit: c.unit || 'lb',
        })),
      createdAt: '', updatedAt: '',
    };
    return calcRecipeCost(fakeRecipe, itemsById);
  }, [editing, itemsById]);

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-white">Recipes</h2>
          <p className="text-sm text-slate-400 mt-0.5">
            Batch recipes — ingredient components, yield, cost, and margin.
          </p>
        </div>
        <button
          onClick={openNew}
          disabled={items.length === 0}
          title={items.length === 0 ? 'Add an item first — recipes need ingredients to reference.' : ''}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-orange-500 hover:bg-orange-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium text-sm transition-colors"
        >
          <Plus size={14} />
          New recipe
        </button>
      </div>

      {items.length === 0 && (
        <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-200 text-sm flex items-center gap-2">
          <AlertCircle size={14} />
          Add items first — recipes pull their ingredients from the Items list.
        </div>
      )}

      {/* Search */}
      <div className="mb-4 relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search recipes by name, description, or category…"
          className="w-full pl-9 pr-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-sm text-white placeholder-slate-500 focus:border-orange-500/50 focus:outline-none"
        />
      </div>

      {/* Table */}
      {recipesWithCost.length === 0 ? (
        <div className="text-center py-16 rounded-2xl bg-slate-800/30 border border-slate-700/40">
          <ChefHat size={32} className="mx-auto text-slate-600 mb-3" />
          <p className="text-slate-400 text-sm">
            {recipes.length === 0 ? 'No recipes yet.' : 'No recipes match your search.'}
          </p>
        </div>
      ) : (
        <div className="rounded-2xl bg-slate-800/30 border border-slate-700/40 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 text-xs uppercase tracking-wider bg-slate-900/40">
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Category</th>
                <th className="px-4 py-3 font-medium text-right">Lines</th>
                <th className="px-4 py-3 font-medium text-right">Total cost</th>
                <th className="px-4 py-3 font-medium text-right">Cost / unit</th>
                <th className="px-4 py-3 font-medium text-right">Sell</th>
                <th className="px-4 py-3 font-medium text-right">Margin</th>
                <th className="px-4 py-3 font-medium w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/40">
              {recipesWithCost.map(({ recipe, totalCost, costPerUnit, hasMissing, margin }) => (
                <tr key={recipe.id} className="hover:bg-slate-700/20">
                  <td className="px-4 py-3">
                    <div className="text-white font-medium flex items-center gap-2">
                      {recipe.name}
                      {hasMissing && (
                        <span title="One or more line items no longer exist">
                          <AlertCircle size={12} className="text-amber-400" />
                        </span>
                      )}
                    </div>
                    {recipe.description && (
                      <div className="text-xs text-slate-500 mt-0.5">{recipe.description}</div>
                    )}
                    <div className="text-xs text-slate-500 mt-0.5">
                      Yield: {recipe.yieldQty} {recipe.yieldUnit}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-300">{recipe.category || '—'}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-300">{recipe.components.length}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-200">${totalCost.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right font-mono text-white">${costPerUnit.toFixed(4)}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-300">
                    {recipe.sellPrice != null ? `$${recipe.sellPrice.toFixed(2)}` : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {margin == null ? (
                      <span className="text-slate-500">—</span>
                    ) : (
                      <span className={`font-mono ${
                        margin > 50 ? 'text-emerald-400' : margin > 30 ? 'text-orange-400' : 'text-red-400'
                      }`}>
                        {margin.toFixed(1)}%
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => { setLabelTarget(recipe); setLabelServing('227g'); setLabelServings('8'); }}
                        className="p-1.5 rounded text-slate-400 hover:text-emerald-400 hover:bg-emerald-500/10"
                        title="Generate nutrition label PDF"
                      >
                        <FileText size={14} />
                      </button>
                      <button
                        onClick={() => openEdit(recipe)}
                        className="p-1.5 rounded text-slate-400 hover:text-blue-400 hover:bg-blue-500/10"
                        title="Edit"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => setConfirmDelete(recipe)}
                        className="p-1.5 rounded text-slate-400 hover:text-red-400 hover:bg-red-500/10"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit modal */}
      {editing && (
        <Modal onClose={() => setEditing(null)} panelClassName="max-w-3xl">
          <h3 className="text-lg font-semibold text-white mb-4">
            {editing.id ? 'Edit recipe' : 'New recipe'}
          </h3>

          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-2">
            <Field label="Name">
              <input
                autoFocus
                type="text"
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                className={INPUT_CLASS}
              />
            </Field>
            <Field label="Description">
              <input
                type="text"
                value={editing.description}
                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                className={INPUT_CLASS}
              />
            </Field>
            <div className="grid grid-cols-4 gap-3">
              <Field label="Category">
                <input
                  type="text"
                  value={editing.category}
                  onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                  className={INPUT_CLASS}
                />
              </Field>
              <Field label="Yield qty">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editing.yieldQty}
                  onChange={(e) => setEditing({ ...editing, yieldQty: e.target.value })}
                  className={`${INPUT_CLASS} font-mono`}
                />
              </Field>
              <Field label="Yield unit">
                <select
                  value={editing.yieldUnit}
                  onChange={(e) => setEditing({ ...editing, yieldUnit: e.target.value })}
                  className={INPUT_CLASS}
                >
                  {UNIT_OPTIONS.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </Field>
              <Field label="Sell price (optional)">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editing.sellPrice}
                  onChange={(e) => setEditing({ ...editing, sellPrice: e.target.value })}
                  placeholder="—"
                  className={`${INPUT_CLASS} font-mono`}
                />
              </Field>
            </div>

            {/* Ingredients */}
            <div className="pt-2">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Ingredients</span>
                <button
                  type="button"
                  onClick={addLine}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-slate-700 hover:bg-slate-600 text-white"
                >
                  <Plus size={12} /> Add line
                </button>
              </div>
              {editing.components.length === 0 ? (
                <div className="text-xs text-slate-500 italic py-2">
                  No ingredients yet — click "Add line".
                </div>
              ) : (
                <div className="space-y-2">
                  {editing.components.map((c, idx) => {
                    const item = itemsById[c.itemId];
                    return (
                      <div key={idx} className="grid grid-cols-[1fr_80px_80px_1fr_auto] gap-2 items-center">
                        <select
                          value={c.itemId}
                          onChange={(e) => updateLine(idx, { itemId: e.target.value })}
                          className={INPUT_CLASS}
                        >
                          <option value="">— Select item —</option>
                          {items.map((it) => (
                            <option key={it.id} value={it.id}>
                              {it.name} (${it.costPerUnit.toFixed(4)}/{it.unit})
                            </option>
                          ))}
                        </select>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={c.qty}
                          onChange={(e) => updateLine(idx, { qty: e.target.value })}
                          className={`${INPUT_CLASS} font-mono`}
                        />
                        <select
                          value={c.unit}
                          onChange={(e) => updateLine(idx, { unit: e.target.value })}
                          className={INPUT_CLASS}
                        >
                          {UNIT_OPTIONS.map((u) => <option key={u} value={u}>{u}</option>)}
                        </select>
                        <input
                          type="text"
                          value={c.notes}
                          onChange={(e) => updateLine(idx, { notes: e.target.value })}
                          placeholder="notes (optional)"
                          className={INPUT_CLASS}
                        />
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono text-slate-400 w-16 text-right">
                            {item ? `$${((parseFloat(c.qty) || 0) * item.costPerUnit).toFixed(2)}` : '—'}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeLine(idx)}
                            className="p-1.5 rounded text-slate-500 hover:text-red-400 hover:bg-red-500/10"
                            title="Remove line"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <Field label="Notes">
              <textarea
                value={editing.notes}
                onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                rows={2}
                className={INPUT_CLASS}
              />
            </Field>
          </div>

          {/* Live cost preview */}
          {previewCost && (
            <div className="mt-4 p-3 rounded-lg bg-slate-900/50 border border-slate-700/40 flex items-center justify-between text-sm">
              <div className="flex items-center gap-4">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Total cost</div>
                  <div className="font-mono text-white">${previewCost.totalCost.toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Cost / {editing.yieldUnit}</div>
                  <div className="font-mono text-white">${previewCost.costPerUnit.toFixed(4)}</div>
                </div>
              </div>
              {previewCost.hasMissing && (
                <div className="text-xs text-amber-400 flex items-center gap-1">
                  <AlertCircle size={12} />
                  One or more items no longer exist
                </div>
              )}
            </div>
          )}

          <div className="mt-6 flex justify-end gap-2">
            <button
              onClick={() => setEditing(null)}
              className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!editing.name.trim()}
              className="px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-400 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium"
            >
              {editing.id ? 'Save changes' : 'Create recipe'}
            </button>
          </div>
        </Modal>
      )}

      {/* Generate label modal */}
      {labelTarget && (
        <Modal onClose={() => setLabelTarget(null)} panelClassName="max-w-md">
          <h3 className="text-lg font-semibold text-white mb-2">Generate nutrition label</h3>
          <p className="text-sm text-slate-400 mb-4">
            FDA standard vertical format for
            <span className="text-white font-medium ml-1">{labelTarget.name}</span>.
            Components without nutrition data contribute 0.
          </p>
          <div className="space-y-3 mb-6">
            <Field label="Serving size (include gram count for accuracy)">
              <input
                autoFocus
                type="text"
                value={labelServing}
                onChange={(e) => setLabelServing(e.target.value)}
                placeholder="227g"
                className={INPUT_CLASS}
              />
              <p className="text-[10px] text-slate-500 mt-1">
                Format: "227g" or "1 cup (240g)" — the gram count drives per-serving scaling.
              </p>
            </Field>
            <Field label="Servings per container">
              <input
                type="number"
                min="1"
                step="1"
                value={labelServings}
                onChange={(e) => setLabelServings(e.target.value)}
                className={`${INPUT_CLASS} font-mono`}
              />
            </Field>
          </div>
          <div className="text-xs text-slate-500 mb-4 italic">
            ⚠️ Labels are regulated artifacts. Validate against current FDA 21 CFR 101.9 spec before commercial printing.
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setLabelTarget(null)}
              className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleGenerateLabel}
              disabled={!labelServing.trim() || parseInt(labelServings, 10) < 1}
              className="px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium"
            >
              Download PDF
            </button>
          </div>
        </Modal>
      )}

      {/* Delete confirm */}
      {confirmDelete && (
        <Modal onClose={() => setConfirmDelete(null)} panelClassName="max-w-sm">
          <h3 className="text-lg font-semibold text-white mb-2">Delete recipe?</h3>
          <p className="text-sm text-slate-400 mb-6">
            Delete <span className="text-white font-medium">{confirmDelete.name}</span>?
          </p>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setConfirmDelete(null)}
              className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              className="px-4 py-2 rounded-lg bg-red-500 hover:bg-red-400 text-white text-sm font-medium"
            >
              Delete
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-400 mb-1">{label}</span>
      {children}
    </label>
  );
}
